// src/context/CartContext.js
import React, { createContext, useContext, useState, useMemo } from "react";
import { LanguageContext } from "./LanguageContext";
import { useEffect } from "react";
import { apiRequest } from "../services/api";
import { getMyWishlist, toggleWishlistApi } from "../services/wishlist.api";
import { getProductBySlug } from "../services/products.api";

const CartContext = createContext();

export function CartProvider({ children }) {
  const { lang } = useContext(LanguageContext);
  
  const [cartItems, setCartItems] = useState([]);
const [cartLoading, setCartLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(
     localStorage.getItem("esill-session")
  );

const [cartShareToken, setCartShareToken] = useState("");

const [bxgySuggestions, setBxgySuggestions] = useState(() => {
  try {
    return JSON.parse(localStorage.getItem("esill-bxgy-suggestions") || "[]");
  } catch {
    return [];
  }
});

  const [authToken, setAuthToken] = useState(
  localStorage.getItem("esill-token")
);

const clearCart = () => {
  setCartItems([]);
};

  // WISHLIST
  const [wishlist, setWishlist] = useState([]);

  // COUPON
  const [couponCode, setCouponCode] = useState("");
  const [discountValue, setDiscountValue] = useState(0);

  // UI STATES (drawer + toast)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [addedToast, setAddedToast] = useState(null); // { nameEN, nameAR }
  
  useEffect(() => {
  const syncAuth = () => {
    setAuthToken(localStorage.getItem("esill-token"));
    setSessionToken(localStorage.getItem("esill-session"));
  };

  // initial sync
  syncAuth();

  // listen to custom auth change event
  window.addEventListener("auth-changed", syncAuth);

  return () => window.removeEventListener("auth-changed", syncAuth);
}, []);


useEffect(() => {
  localStorage.setItem(
    "esill-bxgy-suggestions",
    JSON.stringify(bxgySuggestions)
  );
}, [bxgySuggestions]);

 useEffect(() => {
  if (!authToken) {
    setWishlist([]); // clear wishlist on logout
    return;
  }

  const loadWishlist = async () => {
    try {
      const data = await getMyWishlist();
      setWishlist(
        data.map((p) => ({
          id: p.id,
          nameEN: p.name_en,
          nameAR: p.name_ar,
          price: p.sale_percent
            ? Math.round(p.base_price * (1 - p.sale_percent / 100))
            : p.base_price,
          image: p.image || "/images/placeholder-product.jpg",
        }))
      );
    } catch (err) {
      console.error("Failed to load wishlist", err);
    }
  };

  loadWishlist();
}, [authToken]); // 👈 THIS IS THE FIX

const addBxgyDiscountItem = async (suggestion) => {
  try {
    if (!suggestion) return;

    const headers = getUrlCartToken()
      ? { "x-cart-token": getUrlCartToken() }
      : !authToken && sessionToken
      ? { "x-session-token": sessionToken }
      : {};

    // ✅ SAME PRODUCT PERCENTAGE BXGY
    // Example: Buy 2 Get 1 at 50%
    // CTA may need to add:
    // - remaining normal paid items
    // - discounted GET item
    if (suggestion.action === "complete_percentage_same_product") {
      if (!suggestion.buyProductId || !suggestion.buyVariantId) return;

      const normalQtyToAdd = Number(suggestion.normalQtyToAdd || 0);
      const discountedQtyToAdd = Number(suggestion.discountedQtyToAdd || 0);

      if (normalQtyToAdd > 0) {
        await apiRequest("/cart/items", {
          method: "POST",
          headers,
          body: JSON.stringify({
            product_id: suggestion.buyProductId,
            variant_id: suggestion.buyVariantId,
            qty: normalQtyToAdd,
          }),
        });
      }

      if (discountedQtyToAdd > 0) {
        await apiRequest("/cart/items", {
          method: "POST",
          headers,
          body: JSON.stringify({
            product_id: suggestion.buyProductId,
            variant_id: suggestion.buyVariantId,
            qty: discountedQtyToAdd,
            bxgy_type: "percentage",
            bxgy_discount_value: Number(suggestion.discountValue || 0),
          }),
        });
      }

      setBxgySuggestions((prev) =>
        prev.filter(
          (s) =>
            !(
              Number(s.buyProductId) === Number(suggestion.buyProductId) &&
              Number(s.buyVariantId) === Number(suggestion.buyVariantId) &&
              s.action === "complete_percentage_same_product"
            )
        )
      );

      await loadCart();
      return;
    }

    // ✅ FREE BXGY / normal add-more CTA
if (suggestion.action === "add_buy_more") {
  if (!suggestion.buyProductId || !suggestion.buyVariantId) return;

  // 1) Add the missing BUY/X quantity first
  await apiRequest("/cart/items", {
    method: "POST",
    headers,
    body: JSON.stringify({
      product_id: suggestion.buyProductId,
      variant_id: suggestion.buyVariantId,
      qty: Number(suggestion.missingQty || 1),
    }),
  });

  const isDifferentProductPercentage =
    suggestion.discountType === "percentage" &&
    Number(suggestion.getProductId) !== Number(suggestion.buyProductId);

  // ✅ Keep existing working behavior for:
  // - same product free
  // - same product percentage
  // - different product free
  if (!isDifferentProductPercentage) {
    await loadCart();
    return;
  }

  // 2) For DIFFERENT PRODUCT + PERCENTAGE:
  // after completing X, also add Y as discounted item.
  const res = await apiRequest("/products").catch(() => null);
  const products = res || [];

  const getProduct = products.find(
    (p) => Number(p.id) === Number(suggestion.getProductId)
  );

  if (!getProduct) {
    await loadCart();
    return;
  }

  const fullProduct = await getProductBySlug(
    getProduct.seo_slug
  ).catch(() => null);

  if (!fullProduct) {
    await loadCart();
    return;
  }

  const firstVariant =
    fullProduct.variants?.find((v) => Number(v.stock_qty) > 0) || null;

  if (!firstVariant) {
    await loadCart();
    return;
  }

  await apiRequest("/cart/items", {
    method: "POST",
    headers,
    body: JSON.stringify({
      product_id: suggestion.getProductId,
      variant_id: firstVariant.id,
      qty: Number(suggestion.getQty || 1),
      bxgy_type: "percentage",
      bxgy_discount_value: Number(suggestion.discountValue || 0),
    }),
  });

  setBxgySuggestions((prev) =>
    prev.filter(
      (s) =>
        !(
          Number(s.buyProductId) === Number(suggestion.buyProductId) &&
          Number(s.buyVariantId) === Number(suggestion.buyVariantId) &&
          Number(s.getProductId) === Number(suggestion.getProductId) &&
          s.action === "add_buy_more"
        )
    )
  );

  await loadCart();
  return;
}

    // ✅ DIFFERENT PRODUCT / old percentage fallback
    const res = await apiRequest("/products").catch(() => null);
    const products = res || [];

    const getProduct = products.find(
      (p) => Number(p.id) === Number(suggestion.getProductId)
    );

    if (!getProduct) return;

    const fullProduct = await getProductBySlug(
      getProduct.seo_slug
    ).catch(() => null);

    if (!fullProduct) return;

    const firstVariant =
      fullProduct.variants?.find((v) => Number(v.stock_qty) > 0) || null;

    if (!firstVariant) return;

    const bxgyType =
      suggestion.discountType === "free" ? "free" : "percentage";

    const bxgyDiscountValue =
      bxgyType === "free" ? 100 : Number(suggestion.discountValue || 0);

    await apiRequest("/cart/items", {
      method: "POST",
      headers,
      body: JSON.stringify({
        product_id: suggestion.getProductId,
        variant_id: firstVariant.id,
        qty: Number(suggestion.getQty || 1),
        bxgy_type: bxgyType,
        bxgy_discount_value: bxgyDiscountValue,
      }),
    });

    setBxgySuggestions((prev) =>
      prev.filter(
        (s) =>
          !(
            Number(s.buyProductId) === Number(suggestion.buyProductId) &&
            Number(s.getProductId) === Number(suggestion.getProductId)
          )
      )
    );

    await loadCart();
  } catch (err) {
    console.error(err);
  }
};

const getCartItemMergeKey = (item) => {
  return [
    item.product_id,
    item.variant_id,
    item.bxgyType || "none",
    item.bxgyDiscountRawValue || 0,
  ].join("-");
};

const normalizeCartItems = (items = []) => {
  const map = new Map();

  items.forEach((item) => {
    const key = getCartItemMergeKey(item);

    if (!map.has(key)) {
      map.set(key, {
        ...item,
        cart_item_ids: [item.id],
      });
      return;
    }

    const existing = map.get(key);

    map.set(key, {
      ...existing,
      // ✅ duplicated backend rows should display once
      // ✅ keeps the correct latest quantity in your current issue: 1 + 2 rows => display 2
      qty: Math.max(Number(existing.qty || 0), Number(item.qty || 0)),
      cart_item_ids: [...(existing.cart_item_ids || []), item.id],
    });
  });

  return Array.from(map.values());
};

const getUrlCartToken = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("cart_token") || "";
};

    // ---- CART OPERATIONS ----
  const loadCart = async (tokenOverride) => {
  setCartLoading(true);

  try {
     const headers = {};
const urlCartToken = getUrlCartToken();

if (urlCartToken) {
  headers["x-cart-token"] = urlCartToken;
} else {
  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  if (sessionToken) {
    headers["x-session-token"] = sessionToken;
  }
}

const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const cartUrl = urlCartToken
  ? `${API_URL}/cart?cart_token=${encodeURIComponent(urlCartToken)}`
  : `${API_URL}/cart`;

const res = await fetch(cartUrl, { headers });
      const data = await res.json();
if (!res.ok) {
  console.error("Cart API error:", data);
  return null;
}
      if (data.session_token && !sessionToken) {
        localStorage.setItem("esill-session", data.session_token);
        setSessionToken(data.session_token);
      }

      if (data.share_token) {
  setCartShareToken(data.share_token);
}

const mappedCartItems = (data.items || []).map((it) => {
  const basePrice = Number(it.base_price) || 0;
  const extraPrice = Number(it.extra_price) || 0;

  const finalBase =
    it.on_sale && Number(it.sale_percent) > 0
      ? Math.round(basePrice * (1 - it.sale_percent / 100))
      : basePrice;

  const originalUnitPrice = basePrice + extraPrice;
const rawPrice = finalBase + extraPrice;

let finalPrice = rawPrice;

  if (it.bxgy_type === "free") {
    finalPrice = 0;
  } else if (it.bxgy_type === "percentage") {
    finalPrice = Math.round(
      rawPrice * (1 - Number(it.bxgy_discount_value || 0) / 100)
    );
  }

  return {
    id: it.id,
    product_id: it.product_id,
    variant_id: it.variant_id,
    slug: it.seo_slug,
    public_id: it.public_id,
    nameEN: it.name_en,
    nameAR: it.name_ar,
    price: finalPrice,
    base_price: basePrice,
original_price: originalUnitPrice,
sale_percent: Number(it.sale_percent || 0),
on_sale: !!it.on_sale,
    qty: Number(it.qty || 0),
    image: it.image || "/images/placeholder-product.jpg",
    size: it.size,
    color: it.color_name,
    color_hex: it.color_hex || "#000000",
    stock_qty:
  it.stock_qty === null || it.stock_qty === undefined
    ? null
    : Number(it.stock_qty),

    // ✅ needed for safe duplicate grouping
    bxgyType: it.bxgy_type || "none",
    bxgyDiscountRawValue: Number(it.bxgy_discount_value || 0),

    isBxgyFree: it.bxgy_type === "free",
    isBxgyDiscounted: it.bxgy_type === "percentage",
    bxgyDiscountValue: Number(it.bxgy_discount_value || 0),
  };
});

const normalizedItems = normalizeCartItems(mappedCartItems);

setCartItems(normalizedItems);

const cartSubtotal = normalizedItems.reduce((sum, item) => {
  if (item.isBxgyFree) return sum;
  return sum + Number(item.price || 0) * Number(item.qty || 0);
}, 0);

const cartTotalItems = normalizedItems.reduce(
  (sum, item) => sum + Number(item.qty || 0),
  0
);

// ✅ CLEAN invalid BXGY suggestions
setBxgySuggestions((prev) =>
  prev.filter((s) => {
    const totalNormalQty = (data.items || [])
      .filter(
        (it) =>
          Number(it.product_id) === Number(s.buyProductId) &&
          (!s.buyVariantId ||
            Number(it.variant_id) === Number(s.buyVariantId)) &&
          (it.bxgy_type || "none") === "none"
      )
      .reduce((sum, it) => sum + Number(it.qty || 0), 0);

    const totalDiscountedQty = (data.items || [])
      .filter(
        (it) =>
          Number(it.product_id) === Number(s.buyProductId) &&
          (!s.buyVariantId ||
            Number(it.variant_id) === Number(s.buyVariantId)) &&
          (it.bxgy_type || "none") === "percentage"
      )
      .reduce((sum, it) => sum + Number(it.qty || 0), 0);

    // ✅ remove suggestion if the buy product no longer exists in cart
    if (totalNormalQty <= 0) return false;

    // ✅ keep same-product incomplete suggestion until discounted item exists
    if (s.action === "complete_percentage_same_product") {
      return totalDiscountedQty <= 0 && totalNormalQty < Number(s.buyQty || 0) + Number(s.getQty || 0);
    }

    // ✅ keep incomplete add-more suggestion until condition is completed
    if (s.action === "add_buy_more") {
      return totalNormalQty < Number(s.buyQty || 0);
    }

    // ✅ keep old discounted-get suggestion only after condition is complete
    return totalNormalQty >= Number(s.buyQty || 0);
  })
);

return {
  items: normalizedItems,
  subtotal: cartSubtotal,
  totalItems: cartTotalItems,
};

    } catch (err) {
  console.error("Failed to load cart", err);
  return null;
} finally {
  setCartLoading(false);
}
  };

  // initial load
  useEffect(() => {
  loadCart();
}, [authToken, sessionToken]);

const completeSameProductPercentageBxgy = async ({
  productId,
  variantId,
  buyQty,
  getQty,
  discountValue,
  totalNormalQtyAfterAdd,
}) => {
  const headers = getUrlCartToken()
    ? { "x-cart-token": getUrlCartToken() }
    : !authToken && sessionToken
    ? { "x-session-token": sessionToken }
    : {};

  const cycleQty = Number(buyQty || 0) + Number(getQty || 0);
  if (!cycleQty) return false;

  const completedCycles = Math.floor(Number(totalNormalQtyAfterAdd || 0) / cycleQty);
  if (completedCycles <= 0) return false;

  const discountedTargetQty = completedCycles * Number(getQty || 0);
  const normalTargetQty =
    Number(totalNormalQtyAfterAdd || 0) - discountedTargetQty;

  const normalItem = cartItems.find(
    (it) =>
      Number(it.product_id) === Number(productId) &&
      Number(it.variant_id) === Number(variantId) &&
      (it.bxgyType || "none") === "none"
  );

  const existingDiscountedQty = cartItems
    .filter(
      (it) =>
        Number(it.product_id) === Number(productId) &&
        Number(it.variant_id) === Number(variantId) &&
        (it.bxgyType || "none") === "percentage" &&
        Number(it.bxgyDiscountRawValue || it.bxgyDiscountValue || 0) ===
          Number(discountValue || 0)
    )
    .reduce((sum, it) => sum + Number(it.qty || 0), 0);

  const discountedQtyToAdd = Math.max(
    0,
    discountedTargetQty - existingDiscountedQty
  );

  // ✅ The normal product was already increased first.
  // Now reduce the normal row back to the paid quantity.
  if (normalItem?.id && normalTargetQty > 0) {
    await apiRequest(`/cart/items/${normalItem.id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify({
        qty: normalTargetQty,
        variant_id: variantId,
      }),
    });
  }

  // ✅ Add the discounted unit as a separate cart row.
  if (discountedQtyToAdd > 0) {
    await apiRequest("/cart/items", {
      method: "POST",
      headers,
      body: JSON.stringify({
        product_id: productId,
        variant_id: variantId,
        qty: discountedQtyToAdd,
        bxgy_type: "percentage",
        bxgy_discount_value: Number(discountValue || 0),
      }),
    });
  }

  return true;
};

const convertManualDifferentProductGetBxgy = async ({
  loadedCart,
  buyProductId,
  buyQty,
  getProductId,
  getVariantId,
  getQty,
  discountType,
  discountValue,
}) => {
  const headers = getUrlCartToken()
    ? { "x-cart-token": getUrlCartToken() }
    : !authToken && sessionToken
    ? { "x-session-token": sessionToken }
    : {};

  const items = Array.isArray(loadedCart?.items) ? loadedCart.items : [];

  const totalBuyQty = items
    .filter(
      (it) =>
        Number(it.product_id) === Number(buyProductId) &&
        (it.bxgyType || "none") === "none"
    )
    .reduce((sum, it) => sum + Number(it.qty || 0), 0);

  const qualifiedTimes = Math.floor(
    Number(totalBuyQty || 0) / Number(buyQty || 1)
  );

  if (qualifiedTimes <= 0) return loadedCart;

  const targetGetQty = qualifiedTimes * Number(getQty || 1);

  const bxgyType = discountType === "free" ? "free" : "percentage";
  const bxgyDiscountValue =
    bxgyType === "free" ? 100 : Number(discountValue || 0);

  const existingBxgyGetQty = items
    .filter(
      (it) =>
        Number(it.product_id) === Number(getProductId) &&
        (it.bxgyType || "none") === bxgyType &&
        Number(it.bxgyDiscountRawValue || it.bxgyDiscountValue || 0) ===
          Number(bxgyDiscountValue || 0)
    )
    .reduce((sum, it) => sum + Number(it.qty || 0), 0);

  const qtyStillAllowed = Math.max(0, targetGetQty - existingBxgyGetQty);

  if (qtyStillAllowed <= 0) return loadedCart;

  const normalGetItem = items.find(
    (it) =>
      Number(it.product_id) === Number(getProductId) &&
      Number(it.variant_id) === Number(getVariantId) &&
      (it.bxgyType || "none") === "none"
  );

  if (!normalGetItem?.id) return loadedCart;

  const qtyToConvert = Math.min(
    Number(normalGetItem.qty || 0),
    Number(qtyStillAllowed || 0)
  );

  if (qtyToConvert <= 0) return loadedCart;

  const remainingNormalQty = Number(normalGetItem.qty || 0) - qtyToConvert;

  if (remainingNormalQty > 0) {
    await apiRequest(`/cart/items/${normalGetItem.id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify({
        qty: remainingNormalQty,
        variant_id: getVariantId,
      }),
    });
  } else {
    await apiRequest(`/cart/items/${normalGetItem.id}`, {
      method: "DELETE",
      headers,
    });
  }

  await apiRequest("/cart/items", {
    method: "POST",
    headers,
    body: JSON.stringify({
      product_id: getProductId,
      variant_id: getVariantId,
      qty: qtyToConvert,
      bxgy_type: bxgyType,
      bxgy_discount_value: bxgyDiscountValue,
    }),
  });

  return await loadCart();
};

  const addToCart = async (product, qty = 1) => {
  try {
    const productId = product.product_id ?? product.id;
    const variantId = product.variant_id;

    // ✅ ADD MAIN PRODUCT
    await apiRequest("/cart/items", {
      method: "POST",
      headers:
  getUrlCartToken()
    ? { "x-cart-token": getUrlCartToken() }
    : !authToken && sessionToken
    ? { "x-session-token": sessionToken }
    : {},
      body: JSON.stringify({
        product_id: productId,
        variant_id: variantId,
        qty,
      }),
    });

// =====================================================
// 🎁 BXGY LOGIC START
// =====================================================
if (product?.bxgyDetails?.length) {
  const buyItem = product.bxgyDetails.find((i) => i.role === "buy");
  const getItem = product.bxgyDetails.find((i) => i.role === "get");

  if (!buyItem || !getItem) {
    return await loadCart();
  }

    // ✅ Only the BUY/X product should trigger BXGY logic.
  // The GET/Y product may receive bxgyDetails from backend, but it must not show suggestions.
  const isCurrentProductBuyItem =
  Number(buyItem.product_id) === Number(productId);

const isCurrentProductGetItem =
  Number(getItem.product_id) === Number(productId);

// ✅ If user manually adds Y after already having enough X,
// convert this manually added Y into free/discounted BXGY item.
if (!isCurrentProductBuyItem && isCurrentProductGetItem) {
  const loadedCart = await loadCart();

  return await convertManualDifferentProductGetBxgy({
    loadedCart,
    buyProductId: buyItem.product_id,
    buyQty: Number(buyItem.buy_qty || 0),
    getProductId: getItem.product_id,
    getVariantId: variantId,
    getQty: Number(getItem.get_qty || 0),
    discountType:
      getItem.discount_type || getItem.get_discount_type || "free",
    discountValue: Number(
      getItem.discount_value ?? getItem.get_discount_value ?? 0
    ),
  });
}

if (!isCurrentProductBuyItem) {
  return await loadCart();
}

  const buyQty = Number(buyItem.buy_qty || 0);
  const getQty = Number(getItem.get_qty || 0);

  if (!buyQty || !getQty) {
    return await loadCart();
  }

  const discountType =
    getItem.discount_type || getItem.get_discount_type || "free";

  const discountValue = Number(
    getItem.discount_value ?? getItem.get_discount_value ?? 0
  );

  const isSameProductBxgy =
    Number(getItem.product_id) === Number(productId);

  const existingNormalQty = cartItems
    .filter(
      (it) =>
        Number(it.product_id) === Number(productId) &&
        Number(it.variant_id) === Number(variantId) &&
        (it.bxgyType || "none") === "none"
    )
    .reduce((sum, it) => sum + Number(it.qty || 0), 0);

  const totalNormalQtyAfterAdd = existingNormalQty + Number(qty || 1);

  // =====================================================
  // ✅ SAME PRODUCT + PERCENTAGE
  // Example: Buy 2 Get 1 at 50%
  // Full cycle is 3, not 2.
  // =====================================================
  if (isSameProductBxgy && discountType === "percentage") {
    const cycleQty = buyQty + getQty;
    const missingCycleQty =
      totalNormalQtyAfterAdd % cycleQty === 0
        ? 0
        : cycleQty - (totalNormalQtyAfterAdd % cycleQty);

    // ✅ Not yet completed the cycle.
    // Example after first item:
    // Buy 2 Get 1 50% => Add 2 more.
    if (missingCycleQty > 0) {
      const missingPaidQty = Math.max(0, buyQty - totalNormalQtyAfterAdd);
      const normalQtyToAdd = missingPaidQty;
      const discountedQtyToAdd = Math.max(0, missingCycleQty - normalQtyToAdd);

      const newSuggestion = {
  action: "complete_percentage_same_product",

  buyProductId: productId,
  buyVariantId: variantId,
  getPublicId: getItem.public_id || getItem.publicId || "",
  buyQty,
  getQty,

  missingQty: missingCycleQty,
  normalQtyToAdd,
  discountedQtyToAdd,

  getProductId: productId,
  discountType,
  discountValue,

  nameEN: getItem.name_en || product.nameEN,
  nameAR: getItem.name_ar || product.nameAR,
  image: getItem.image || product.image || "/images/placeholder-product.jpg",
};

// ✅ Save incomplete BXGY suggestion so CartPage can show it
setBxgySuggestions((prev) => {
  const filtered = prev.filter(
    (s) =>
      !(
        Number(s.buyProductId) === Number(productId) &&
        Number(s.buyVariantId) === Number(variantId) &&
        s.action === "complete_percentage_same_product"
      )
  );

  return [...filtered, newSuggestion];
});

const loadedCart = await loadCart();

return {
  ...loadedCart,
  bxgySuggestion: newSuggestion,
};
    }

    // ✅ Cycle completed manually.
    // Example: user had 2 normal items and added 1 more.
    // Convert the last one into discounted item.
    const converted = await completeSameProductPercentageBxgy({
      productId,
      variantId,
      buyQty,
      getQty,
      discountValue,
      totalNormalQtyAfterAdd,
    });

    if (converted) {
      return await loadCart();
    }

    return await loadCart();
  }

  // =====================================================
  // ✅ FREE BXGY - keep your working behavior
  // =====================================================
  if (discountType === "free") {
    const missingQty = Math.max(0, buyQty - totalNormalQtyAfterAdd);
    const qualifiedTimes = Math.floor(totalNormalQtyAfterAdd / buyQty);

    if (missingQty > 0) {
      const newSuggestion = {
  action: "add_buy_more",

  buyProductId: productId,
  buyVariantId: variantId,
  buyQty,
  missingQty,

  getProductId: getItem.product_id,
  getPublicId: getItem.public_id || getItem.publicId || "",
  getQty,
  discountType,
  discountValue,

  nameEN: getItem.name_en,
  nameAR: getItem.name_ar,
  image: getItem.image || product.image || "/images/placeholder-product.jpg",
};

// ✅ Save incomplete BXGY suggestion so CartPage can show it
setBxgySuggestions((prev) => {
  const filtered = prev.filter(
    (s) =>
      !(
        Number(s.buyProductId) === Number(productId) &&
        Number(s.buyVariantId) === Number(variantId) &&
        Number(s.getProductId) === Number(getItem.product_id) &&
        s.action === "add_buy_more"
      )
  );

  return [...filtered, newSuggestion];
});

const loadedCart = await loadCart();

return {
  ...loadedCart,
  bxgySuggestion: newSuggestion,
};
    }

    if (qualifiedTimes > 0) {
      return await loadCart();
    }
  }

  // =====================================================
  // ✅ DIFFERENT PRODUCT + PERCENTAGE fallback
  // Keep old behavior for non-same-product offers.
  // =====================================================
  if (!isSameProductBxgy && discountType === "percentage") {
    const missingQty = Math.max(0, buyQty - totalNormalQtyAfterAdd);
    const qualifiedTimes = Math.floor(totalNormalQtyAfterAdd / buyQty);

    if (missingQty > 0) {
      const newSuggestion = {
  action: "add_buy_more",

  buyProductId: productId,
  buyVariantId: variantId,
  buyQty,
  missingQty,

  getProductId: getItem.product_id,
  getPublicId: getItem.public_id || getItem.publicId || "",
  getQty,
  discountType,
  discountValue,

  nameEN: getItem.name_en,
  nameAR: getItem.name_ar,
  image: getItem.image || product.image || "/images/placeholder-product.jpg",
};

// ✅ Save incomplete BXGY suggestion so CartPage can show it
setBxgySuggestions((prev) => {
  const filtered = prev.filter(
    (s) =>
      !(
        Number(s.buyProductId) === Number(productId) &&
        Number(s.buyVariantId) === Number(variantId) &&
        Number(s.getProductId) === Number(getItem.product_id) &&
        s.action === "add_buy_more"
      )
  );

  return [...filtered, newSuggestion];
});

const loadedCart = await loadCart();

return {
  ...loadedCart,
  bxgySuggestion: newSuggestion,
};
    }

    if (qualifiedTimes > 0) {
      const newSuggestion = {
        action: "add_discounted_get",

        buyProductId: productId,
        buyVariantId: variantId,
        buyQty,

        getProductId: getItem.product_id,
        getPublicId: getItem.public_id || getItem.publicId || "",
        getQty,
        discountType,
        discountValue,

        nameEN: getItem.name_en,
        nameAR: getItem.name_ar,
        image: getItem.image || "/images/placeholder-product.jpg",
      };

      setBxgySuggestions((prev) => {
        const exists = prev.some(
          (s) =>
            Number(s.buyProductId) === Number(productId) &&
            Number(s.getProductId) === Number(getItem.product_id) &&
            s.action === "add_discounted_get"
        );

        if (exists) return prev;

        return [...prev, newSuggestion];
      });

      const loadedCart = await loadCart();

      return {
        ...loadedCart,
        bxgySuggestion: newSuggestion,
      };
    }
  }
}
// =====================================================
// 🎁 BXGY LOGIC END
// =====================================================

return await loadCart();
  } catch (err) {
    console.error(err);
  }
};

 const removeFromCart = async (id) => {
  try {
    const itemToRemove = cartItems.find((x) => Number(x.id) === Number(id));

    const idsToRemove =
      Array.isArray(itemToRemove?.cart_item_ids) &&
      itemToRemove.cart_item_ids.length > 0
        ? itemToRemove.cart_item_ids
        : [id];

    for (const cartItemId of idsToRemove) {
      await apiRequest(`/cart/items/${cartItemId}`, {
        method: "DELETE",
        headers:
  getUrlCartToken()
    ? { "x-cart-token": getUrlCartToken() }
    : !authToken && sessionToken
    ? { "x-session-token": sessionToken }
    : {},
      });
    }

    await loadCart();
  } catch (err) {
    console.error(err);
  }
};

  const changeQty = async (id, delta) => {
  const item = cartItems.find((x) => x.id === id);
  if (!item) return;

  const newQty = Math.max(1, item.qty + delta);

  try {
    await apiRequest(`/cart/items/${id}`, {
      method: "PUT",
      headers:
  getUrlCartToken()
    ? { "x-cart-token": getUrlCartToken() }
    : !authToken && sessionToken
    ? { "x-session-token": sessionToken }
    : {},
      body: JSON.stringify({ qty: newQty }),
    });

    // ✅ ONLY reload cart (backend handles BXGY)
    await loadCart();
  } catch (err) {
    console.error(err);
  }
};

const updateCartItem = async (item, updates = {}) => {
  if (!item?.id) return;

  try {
    const ids =
      Array.isArray(item.cart_item_ids) && item.cart_item_ids.length > 0
        ? item.cart_item_ids
        : [item.id];

    const primaryId = ids[0];

    await apiRequest(`/cart/items/${primaryId}`, {
      method: "PUT",
      headers:
  getUrlCartToken()
    ? { "x-cart-token": getUrlCartToken() }
    : !authToken && sessionToken
    ? { "x-session-token": sessionToken }
    : {},
      body: JSON.stringify({
        qty: Math.max(1, Number(updates.qty || item.qty || 1)),
        variant_id: updates.variant_id || item.variant_id,
      }),
    });

    // remove duplicated grouped backend rows safely
    for (const duplicateId of ids.slice(1)) {
      await apiRequest(`/cart/items/${duplicateId}`, {
        method: "DELETE",
       headers:
  getUrlCartToken()
    ? { "x-cart-token": getUrlCartToken() }
    : !authToken && sessionToken
    ? { "x-session-token": sessionToken }
    : {},
      });
    }

    await loadCart();
  } catch (err) {
    console.error(err);
  }
};

  // ---- WISHLIST ----
  const isInWishlist = (id) => wishlist.some((item) => item.id === id);

 const toggleWishlist = async (item) => {
  // 🔁 SYNC MODE (used by WishlistPage)
  if (item.__sync) {
    setWishlist(
      item.items.map((p) => ({
        id: p.id,
        nameEN: p.name_en,
        nameAR: p.name_ar,
        price: p.sale_percent
          ? Math.round(p.base_price * (1 - p.sale_percent / 100))
          : p.base_price,
        image: p.image || "/images/placeholder-product.jpg",
      }))
    );
    return;
  }

  const token = localStorage.getItem("esill-token");

  if (token) {
    try {
      await toggleWishlistApi(item.id);

      setWishlist((prev) => {
        const exists = prev.some((w) => w.id === item.id);
        return exists
          ? prev.filter((w) => w.id !== item.id)
          : [...prev, item];
      });
    } catch (err) {
      console.error("Wishlist API error", err);
    }
    return;
  }

  // guest
  setWishlist((prev) => {
    const exists = prev.some((w) => w.id === item.id);
    return exists
      ? prev.filter((w) => w.id !== item.id)
      : [...prev, item];
  });
};



  // ---- COUPON LOGIC ----
  const applyCoupon = (code) => {
    const trimmed = code.trim().toUpperCase();
    setCouponCode(trimmed);

    // Simple demo rule
    if (trimmed === "ESILL10") {
      // 10% off subtotal
      setDiscountValue((prev) => prev); // will be recomputed from subtotal below
    } else {
      setDiscountValue(0);
    }
  };

  // PRICES
  const subtotal = useMemo(() => {
  return cartItems.reduce((sum, item) => {
    // 🎁 FREE ITEMS → always 0
    if (item.isBxgyFree) {
      return sum;
    }

    // 🎯 DISCOUNTED ITEMS → already correct price
    return sum + item.price * item.qty;
  }, 0);
}, [cartItems]);

  const computedDiscount = useMemo(() => {
    if (couponCode === "ESILL10") {
      return Math.round(subtotal * 0.1);
    }
    return 0;
  }, [couponCode, subtotal]);

  const total = Math.max(0, subtotal - computedDiscount);

  // ---- DRAWER ----
  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);

  const value = {
  cartItems,
  cartLoading,
  cartShareToken,
  wishlist,
  isInWishlist,
  toggleWishlist,
  addToCart,
  removeFromCart,
  changeQty,
  updateCartItem,
  clearCart,
  couponCode,
  applyCoupon,
  subtotal,
  discount: computedDiscount,
  total,
  isDrawerOpen,
  openDrawer,
  closeDrawer,
  addedToast,
  lang,
  addBxgyDiscountItem,
  bxgySuggestions,
  setBxgySuggestions,
};

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  return useContext(CartContext);
}